/**
 * Structured logger utility for consistent logging across services
 * 
 * Features:
 * - Structured JSON logging with context preservation
 * - Performance tracking with spans and timing
 * - Metrics aggregation (operations, errors, response times)
 * - Request lifecycle tracking
 * - Debug endpoints for monitoring
 * - Error tracking and analysis
 * 
 * @example
 * const logger = createLogger('UserService')
 * logger.info('User created', { userId: '123' })
 * logger.error('Failed to update user', { userId: '123', error })
 * 
 * // Performance tracking
 * const spanId = logger.startSpan('createUser', { userId })
 * // ... do work ...
 * logger.endSpan(spanId, { success: true })
 */

export interface LogContext {
  [key: string]: unknown
}

interface Span {
  spanId: string
  operation: string
  startTime: number
  context?: LogContext
}

interface Metrics {
  operationCount: number
  totalDuration: number
  errorCount: number
  avgDuration: number
  lastError?: { message: string; timestamp: string }
}

const MAX_CONTEXT_DEPTH = 2
const MAX_STACK_FRAMES = Number(process.env.LOG_STACK_FRAMES ?? '5')
const MAX_OBJECT_KEYS = 20
const CAPTURE_CALLER = process.env.LOG_CALLER !== 'false'
type CallerInfo = { file?: string; line?: number; column?: number; frame?: string }

function trimStack(stack?: string, maxFrames: number = MAX_STACK_FRAMES): string | undefined {
  if (!stack) return undefined

  const lines = stack.split('\n')
  if (lines.length === 0) return undefined

  const header = lines[0]
  const frames = lines.slice(1).filter((line) => {
    const lower = line.toLowerCase()
    return !(
      lower.includes('node:internal') ||
      lower.includes('node_modules/pino') ||
      lower.includes('fastify/lib') ||
      lower.includes('diagnostics_channel') ||
      lower.includes('async_hooks')
    )
  })

  const limitedFrames = frames.slice(0, maxFrames)
  return [header, ...limitedFrames].join('\n')
}

function extractCaller(): CallerInfo | undefined {
  if (!CAPTURE_CALLER) return undefined

  const stack = new Error().stack
  if (!stack) return undefined

  const frames = stack.split('\n').slice(2) // drop "Error" + extractCaller frames
  const callerFrame = frames.find((line) => {
    const lower = line.toLowerCase()
    return !(
      lower.includes('common/utils/logger') ||
      lower.includes('node:internal') ||
      lower.includes('node_modules/pino') ||
      lower.includes('diagnostics_channel') ||
      lower.includes('async_hooks')
    )
  })

  if (!callerFrame) return undefined

  const match = callerFrame.match(/(?:\(|\s)([^()]+?):(\d+):(\d+)\)?$/)
  if (match) {
    return {
      file: match[1],
      line: Number(match[2]),
      column: Number(match[3]),
      frame: callerFrame.trim()
    }
  }

  return { frame: callerFrame.trim() }
}

function serializeValue(value: unknown, depth: number = MAX_CONTEXT_DEPTH): unknown {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: trimStack(value.stack),
      ...(value.cause instanceof Error ? { cause: value.cause.message } : {})
    }
  }

  if (typeof value === 'string') {
    const maybeStack = trimStack(value)
    return maybeStack ?? value
  }

  if (depth <= 0) {
    if (Array.isArray(value)) return '[array]'
    if (value && typeof value === 'object') return '[object]'
    return value
  }

  if (Array.isArray(value)) {
    return value.slice(0, MAX_OBJECT_KEYS).map((item) => serializeValue(item, depth - 1))
  }

  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).slice(0, MAX_OBJECT_KEYS)
    const normalized: Record<string, unknown> = {}
    for (const [key, val] of entries) {
      normalized[key] = serializeValue(val, depth - 1)
    }
    return normalized
  }

  return value
}

function normalizeContext(context?: LogContext): LogContext | undefined {
  if (!context) return undefined
  return serializeValue(context) as LogContext
}

export interface Logger {
  debug(message: string, context?: LogContext): void
  info(message: string, context?: LogContext): void
  warn(message: string, context?: LogContext): void
  error(message: string, context?: LogContext): void
  startSpan(operation: string, context?: LogContext): string
  endSpan(spanId: string, result?: LogContext): void
  startRequest(requestId: string, method: string, url: string, userId?: string): void
  endRequest(requestId: string, statusCode: number, responseSize?: number): void
  getMetrics(): Metrics
  getMetricsSnapshot(): Record<string, Metrics>
  exportLogs(limit?: number): unknown[]
}

export function createLogger(service: string): Logger {
  const LEVELS: Record<string, number> = {
    debug: 10,
    info: 20,
    warn: 30,
    error: 40
  }

  // Default to 'info' even in dev to reduce noise - use LOG_LEVEL=debug explicitly if needed
  const envLogLevel = (process.env.LOG_LEVEL || 'info').toLowerCase()
  const threshold = LEVELS[envLogLevel] ?? LEVELS.info

  const spans = new Map<string, Span>()
  type LogEntry = {
    timestamp: string
    level: string
    service: string
    message: string
    caller?: CallerInfo
  } & LogContext

  const logBuffer: LogEntry[] = []
  const metrics: Metrics = {
    operationCount: 0,
    totalDuration: 0,
    errorCount: 0,
    avgDuration: 0
  }
  const requests = new Map<string, {
    method: string
    url: string
    userId?: string
    startTime: number
    statusCode?: number
    responseSize?: number
  }>()

  const log = (level: string, message: string, context?: LogContext) => {
    const levelValue = LEVELS[level] ?? LEVELS.info
    if (levelValue < threshold) {
      return
    }

    const safeContext = normalizeContext(context)
    const timestamp = new Date().toISOString()
    const caller = extractCaller()
    const logEntry = {
      timestamp,
      level,
      service,
      message,
      ...(caller ? { caller } : {}),
      ...(safeContext ?? {})
    }

    // Keep last 1000 logs in memory
    logBuffer.push(logEntry)
    if (logBuffer.length > 1000) {
      logBuffer.shift()
    }

    // Track errors
    if (level === 'error') {
      metrics.errorCount++
      metrics.lastError = { message, timestamp }
    }

    // In production, this would integrate with Winston/Pino
    // For now, structured console logging
    const baseLogger = console
    const logMethod = level === 'error' ? baseLogger.error : baseLogger.log
    
    if (process.env.NODE_ENV === 'development') {
      // Human-readable in dev with color coding
      const levelColors: Record<string, string> = {
        debug: '\x1b[36m', // Cyan
        info: '\x1b[32m',  // Green
        warn: '\x1b[33m',  // Yellow
        error: '\x1b[31m'  // Red
      }
      const reset = '\x1b[0m'
      const color = levelColors[level] || ''
      
      const contextStr = safeContext && Object.keys(safeContext).length > 0 
        ? `\n  ${JSON.stringify(safeContext, null, 2)}` 
        : ''
      const callerHint =
        caller?.file && caller?.line
          ? ` (${caller.file.split('/').slice(-3).join('/')}:${caller.line})`
          : caller?.frame
            ? ` (${caller.frame.replace(/^at\s+/, '')})`
            : ''
      
      logMethod(
        `${color}[${timestamp}] [${level.toUpperCase().padEnd(5)}] [${service}]${callerHint}${reset} ${message}${contextStr}`
      )
    } else {
      // JSON in production (for log aggregation)
      logMethod(JSON.stringify(logEntry))
    }
  }

  const startSpan = (operation: string, context?: LogContext): string => {
    const spanId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
    spans.set(spanId, {
      spanId,
      operation,
      startTime: Date.now(),
      context
    })
    log('debug', `SPAN_START: ${operation}`, { spanId, ...context })
    return spanId
  }

  const endSpan = (spanId: string, result?: LogContext): void => {
    const span = spans.get(spanId)
    if (!span) {
      log('warn', `SPAN_END: unknown spanId ${spanId}`, result)
      return
    }

    const duration = Date.now() - span.startTime
    metrics.operationCount++
    metrics.totalDuration += duration
    metrics.avgDuration = Math.round(metrics.totalDuration / metrics.operationCount)

    const logLevel = duration > 1000 ? 'warn' : 'debug'
    log(logLevel, `SPAN_END: ${span.operation} (${duration}ms)`, {
      spanId,
      operation: span.operation,
      durationMs: duration,
      ...result
    })

    spans.delete(spanId)
  }

  const startRequest = (requestId: string, method: string, url: string, userId?: string): void => {
    requests.set(requestId, {
      method,
      url,
      userId,
      startTime: Date.now()
    })
    log('debug', `REQUEST_START: ${method} ${url}`, { requestId, userId })
  }

  const endRequest = (requestId: string, statusCode: number, responseSize?: number): void => {
    const request = requests.get(requestId)
    if (!request) {
      log('warn', `REQUEST_END: unknown requestId ${requestId}`)
      return
    }

    const duration = Date.now() - request.startTime
    const logLevel = statusCode >= 400 ? 'warn' : 'info'
    log(logLevel, `REQUEST_END: ${request.method} ${request.url}`, {
      requestId,
      statusCode,
      durationMs: duration,
      responseSize
    })

    requests.delete(requestId)
  }

  return {
    debug: (message, context) => log('debug', message, context),
    info: (message, context) => log('info', message, context),
    warn: (message, context) => log('warn', message, context),
    error: (message, context) => log('error', message, context),
    startSpan,
    endSpan,
    startRequest,
    endRequest,
    getMetrics: () => ({ ...metrics }),
    getMetricsSnapshot: () => ({ [service]: { ...metrics } }),
    exportLogs: (limit?: number) => logBuffer.slice(-(limit || 100))
  }
}

/**
 * Performance tracking decorator
 * 
 * @example
 * async function slowOperation() {
 *   return await trackPerformance('slowOperation', async () => {
 *     // ... your code
 *   }, logger)
 * }
 */
export async function trackPerformance<T>(
  operation: string,
  fn: () => Promise<T>,
  logger: Logger,
  warnThreshold: number = 1000 // ms
): Promise<T> {
  const startTime = Date.now()
  
  try {
    const result = await fn()
    const duration = Date.now() - startTime
    
    const logMethod = duration > warnThreshold ? logger.warn : logger.debug
    logMethod(`${operation} completed`, { duration, operation })
    
    return result
  } catch (error) {
    const duration = Date.now() - startTime
    logger.error(`${operation} failed`, { duration, operation, error })
    throw error
  }
}
